import { useState, useEffect, useCallback } from 'react';
import { createCrawledProductData } from '../types/product';
import { apiClient } from '../utils/apiErrorHandling';

const useProductDetail = (productId) => {
  const [product, setProduct] = useState(createCrawledProductData());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProductDetail = useCallback(async () => {
    if (!productId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiClient.get(`/products/${productId}`);
      console.log('Product Detail API 응답:', result.data);
      
      const productData = result.data;
      
      if (productData) {
        setProduct(productData);
        setLoading(false);
      } else {
        console.error('제품 데이터가 없습니다:', result.data);
        setError('제품 정보를 찾을 수 없습니다.');
        setLoading(false);
      }
    } catch (error) {
      console.error('제품 상세 정보 불러오기 에러:', error);
      setError(error.response?.data?.message || '제품 상세 정보를 불러오는데 실패했습니다.');
      setLoading(false);
    }
  }, [productId]);


  /**
   * Shopify에서 제품 상태 확인 및 DB 업데이트
   * @param {Object} product - 제품 정보
   * @param {Function} updateProductDataFn - 제품 업데이트 함수
   * @returns {Promise<Object|null>} 업데이트된 제품 정보 또는 null
   */
  const checkAndUpdateShopifyStatus = useCallback(async (product, updateProductDataFn) => {
    // shopify_product_id가 없으면 스킵
    if (!product || !product.shopify_product_id || !updateProductDataFn) {
      console.log('Shopify 상태 확인 스킵:', !product ? '제품 없음' : !product.shopify_product_id ? 'shopify_product_id 없음' : 'updateProductDataFn 없음');
      return null;
    }

    try {
      console.log('Shopify 상태 확인 시작:', { productId: product.id, shopifyProductId: product.shopify_product_id, currentStatus: product.status });
      
      // Shopify API에서 제품 정보 조회
      const shopifyResult = await apiClient.get(`/shopify/products/${product.id}`);
      const shopifyData = shopifyResult.data;
      
      console.log('Shopify API 응답:', shopifyData);
      
      // Shopify 응답에서 status 확인
      const shopifyStatus = shopifyData?.status;
      
      console.log('상태 비교:', { 
        dbStatus: product.status, 
        shopifyStatus,
        dbStatusType: typeof product.status,
        shopifyStatusType: typeof shopifyStatus,
        areEqual: shopifyStatus === product.status
      });
      
      // DB의 status와 비교 (null/undefined 처리)
      const dbStatus = product.status || null;
      const shopifyStatusValue = shopifyStatus || null;
      
      // DB의 status와 비교
      if (shopifyStatusValue && shopifyStatusValue !== dbStatus) {
        console.log(`상태 변화 감지: DB(${dbStatus}) -> Shopify(${shopifyStatusValue})`);
        console.log('updateProductDataFn 호출 준비:', typeof updateProductDataFn);
        
        try {
          console.log('updateProductDataFn 호출 시작...', { productId: product.id, newStatus: shopifyStatusValue });
          const updateResult = await updateProductDataFn(product, { status: shopifyStatusValue }, null);
          
          console.log('업데이트 결과:', updateResult);
          console.log('업데이트 결과 타입:', typeof updateResult);
          console.log('업데이트 성공 여부:', updateResult?.success);
          
          if (updateResult && updateResult.success) {
            console.log('✅ DB 상태가 Shopify 상태로 업데이트되었습니다.');
            // 업데이트된 제품 정보 반환 (상태만 업데이트)
            return {
              ...product,
              status: shopifyStatusValue
            };
          } else {
            console.error('❌ DB 업데이트 실패:', updateResult);
            if (updateResult?.error) {
              console.error('업데이트 에러 상세:', updateResult.error);
            }
            if (updateResult?.message) {
              console.error('업데이트 메시지:', updateResult.message);
            }
            return null;
          }
        } catch (updateError) {
          console.error('❌ updateProductDataFn 호출 중 예외 발생:', updateError);
          console.error('예외 상세:', updateError.message, updateError.stack);
          return null;
        }
      } else {
        if (!shopifyStatus) {
          console.log('Shopify status가 없으므로 업데이트하지 않습니다.');
        } else {
          console.log(`상태가 동일하므로 업데이트하지 않습니다. (DB: ${dbStatus}, Shopify: ${shopifyStatusValue})`);
        }
        return null;
      }
    } catch (error) {
      // 에러가 발생해도 조용히 처리 (로그만 출력)
      console.error('Shopify 상태 확인 중 오류:', error.response?.data || error.message);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchProductDetail();
  }, [fetchProductDetail]);

  return { product, loading, error, refetch: fetchProductDetail, checkAndUpdateShopifyStatus };
};

export default useProductDetail;
